/**
 * Profile Page
 * Self-service profile management for all user roles
 * Users can update: firstName, lastName, phoneNumber
 * Users can change password (with current password verification)
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuthStore } from '@/stores/authStore';
import { profileApi, authApi } from '@/services/api';
import { useToast } from '@/contexts/ToastContext';
import { formatPhoneNumber, normalizePhoneNumber } from '@/utils/phoneNumber';
import { getRoleLabel } from '@/utils/roleLabels';
import { getProfileUpdateSchema, getPasswordChangeSchema } from '@/validation/profileSchemas';
import type { ProfileUpdateFormData } from '@/validation/profileSchemas';
import { getContainerStyle } from '@/styles/containers';
import { buttonStyles } from '@/styles/buttons';
import { ThemeToggle } from '@/components/ThemeToggle';

export const Profile: React.FC = () => {
  const { t } = useTranslation(['parent', 'common']);
  usePageTitle(t('profile.pageTitle'));
  const { isMobile } = useMediaQuery();
  const { user, logout } = useAuthStore();
  const { success, error: showError } = useToast();

  // Profile form state
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [phoneNumber, setPhoneNumber] = useState(formatPhoneNumber(user?.phoneNumber || ''));
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Email change state (parent-only)
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [confirmNewEmail, setConfirmNewEmail] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState('');

  // Resend verification state (parent-only)
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const isParent = user?.role === 'parent';

  // Container style from centralized containers module
  const containerStyle = getContainerStyle('medium', isMobile);

  const headerStyle: React.CSSProperties = {
    marginBottom: isMobile ? '1.5rem' : '2rem',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: isMobile ? '1.5rem' : '2rem',
    fontWeight: '700',
    color: 'var(--text-color)',
    marginBottom: '0.5rem',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: isMobile ? '0.875rem' : '1rem',
    color: 'var(--text-secondary)',
    marginBottom: '0',
  };

  const sectionStyle: React.CSSProperties = {
    backgroundColor: 'var(--surface-color)',
    padding: isMobile ? '1rem' : '1.5rem',
    borderRadius: 'var(--radius)',
    marginBottom: isMobile ? '1.5rem' : '2rem',
    boxShadow: 'var(--shadow)',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: isMobile ? '1.125rem' : '1.25rem',
    fontWeight: '600',
    color: 'var(--text-color)',
    marginBottom: isMobile ? '1rem' : '1.25rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid var(--border-color)',
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? '1rem' : '1.25rem',
  };

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    // gap: '0.5rem',
  };

  const inputStyle: React.CSSProperties = {
    padding: isMobile ? '0.625rem' : '0.75rem',
    fontSize: isMobile ? '0.875rem' : '1rem',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--input-bg)',
    color: 'var(--text-color)',
    minHeight: '36px',
  };

  const disabledInputStyle: React.CSSProperties = {
    ...inputStyle,
    backgroundColor: 'var(--disabled-bg)',
    color: 'var(--text-secondary)',
    cursor: 'not-allowed',
  };

  const infoTextStyle: React.CSSProperties = {
    fontSize: isMobile ? '0.8125rem' : '0.875rem',
    color: 'var(--text-secondary)',
    marginTop: '0.5rem',
  };

  const profileSchema = getProfileUpdateSchema(t);
  const passwordSchema = getPasswordChangeSchema(t);

  if (!user) {
    return <div style={containerStyle}>{t('common:status.loading')}</div>;
  }

  /**
   * Handle profile update
   */
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingProfile(true);

    try {
      // Prepare update data
      const updates: ProfileUpdateFormData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim() || undefined,
      };

      // Validate with Zod
      const validated = profileSchema.parse(updates);

      // Normalize phone number for API
      const normalizedPhone = validated.phoneNumber ? normalizePhoneNumber(validated.phoneNumber) : undefined;
      const apiUpdates = {
        firstName: validated.firstName,
        lastName: validated.lastName,
        phoneNumber: normalizedPhone ?? undefined, // Convert null to undefined
      };

      // Call API
      const response = await profileApi.updateProfile(apiUpdates);

      if (response.success) {
        success(t('profile.info.updateSuccess'));

        // Refresh user data
        const meResponse = await authApi.getMe();
        if (meResponse.success && meResponse.user) {
          // Update auth store (will be done automatically by auth middleware)
          setFirstName(meResponse.user.firstName || '');
          setLastName(meResponse.user.lastName || '');
          setPhoneNumber(formatPhoneNumber(meResponse.user.phoneNumber || ''));
        }
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : t('profile.info.updateFailed'));
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  /**
   * Handle email change (parent-only)
   */
  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newEmail !== confirmNewEmail) {
      showError(t('profile.email.emailsDoNotMatch'));
      return;
    }

    setIsChangingEmail(true);
    setPendingEmailChange('');

    try {
      const response = await profileApi.changeEmail(newEmail, confirmNewEmail);
      if (response.success) {
        setPendingEmailChange(newEmail);
        setNewEmail('');
        setConfirmNewEmail('');
        setShowEmailChange(false);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : t('profile.email.changeFailed'));
    } finally {
      setIsChangingEmail(false);
    }
  };

  /**
   * Handle resend verification email
   */
  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await authApi.resendVerification();
      setResendSuccess(true);
      success(t('profile.email.resendSuccess'));
    } catch (err) {
      showError(err instanceof Error ? err.message : t('profile.email.resendFailed'));
    } finally {
      setResendingVerification(false);
    }
  };

  /**
   * Handle password change
   */
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsChangingPassword(true);

    // Validate with Zod schema
    const validation = passwordSchema.safeParse({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    if (!validation.success) {
      const firstError = validation.error.issues[0];
      showError(firstError.message);
      setIsChangingPassword(false);
      return;
    }

    try {
      // Call API
      const response = await profileApi.changePassword(currentPassword, newPassword);

      if (response.success) {
        // Clear password fields
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');

        // Show success message and logout after brief delay
        success(t('profile.password.changeSuccess'));

        // Brief delay so user can read the message before redirect
        setTimeout(() => {
          logout();
        }, 2000);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : t('profile.password.changeFailed'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>{t('profile.title')}</h1>
        <p style={subtitleStyle}>{t('profile.subtitle')}</p>
      </div>

      {/* Profile Information Section (hidden for parents — PATCH /api/profile uses staff table) */}
      {!isParent && (
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>{t('profile.info.title')}</h2>

          <form onSubmit={handleProfileUpdate} style={formStyle}>
            {/* First Name */}
            <div style={formGroupStyle}>
              <label htmlFor="firstName" className="form-label">
                {t('common:form.firstName')} <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={inputStyle}
                required
                disabled={isUpdatingProfile}
              />
            </div>

            {/* Last Name */}
            <div style={formGroupStyle}>
              <label htmlFor="lastName" className="form-label">
                {t('common:form.lastName')} <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={inputStyle}
                required
                disabled={isUpdatingProfile}
              />
            </div>

            {/* Phone Number */}
            <div style={formGroupStyle}>
              <label htmlFor="phoneNumber" className="form-label">
                {t('common:form.phoneNumber')}
              </label>
              <input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={t('profile.info.phonePlaceholder')}
                style={inputStyle}
                disabled={isUpdatingProfile}
              />
              <p style={infoTextStyle}>{t('profile.info.phoneFormat')}</p>
            </div>

            {/* Email (Read Only) */}
            <div style={formGroupStyle}>
              <label htmlFor="email" className="form-label">
                {t('profile.info.email')}
              </label>
              <input id="email" type="email" value={user.email} style={disabledInputStyle} disabled />
              <p style={infoTextStyle}>{t('profile.info.emailCannotChange')}</p>
            </div>

            {/* Role (Read Only) */}
            <div style={formGroupStyle}>
              <label htmlFor="role" className="form-label">
                {t('profile.info.role')}
              </label>
              <input id="role" type="text" value={getRoleLabel(user.role)} style={disabledInputStyle} disabled />
              <p style={infoTextStyle}>{t('profile.info.roleAssignedByAdmins')}</p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              style={{
                ...(isMobile ? buttonStyles.primarySmall : buttonStyles.primary),
                fontWeight: '600',
                alignSelf: 'flex-start',
              }}
              disabled={isUpdatingProfile}
              onMouseEnter={(e) => {
                if (!isUpdatingProfile) {
                  e.currentTarget.style.opacity = '0.9';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              {isUpdatingProfile ? t('profile.info.updating') : t('profile.info.updateButton')}
            </button>
          </form>
        </div>
      )}

      {/* Email Address Section (parent-only) */}
      {isParent && (
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>{t('profile.email.title')}</h2>

          <div style={formGroupStyle}>
            <label className="form-label">{t('profile.email.currentEmail')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input type="email" value={user.email} style={disabledInputStyle} disabled />
              {user.emailVerified ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    alignSelf: 'flex-end',
                    gap: '0.25rem',
                    padding: '0.25rem 0.625rem',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    borderRadius: '999px',
                    backgroundColor: 'var(--success-bg)',
                    color: 'var(--success-text)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('profile.email.verified')}
                </span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-end', gap: '0.5rem' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.625rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      borderRadius: '999px',
                      backgroundColor: 'var(--warning-bg)',
                      color: 'var(--warning-text)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('profile.email.unverified')}
                  </span>
                  {resendSuccess ? (
                    <span style={{ fontSize: '0.75rem', color: 'var(--success-text)', whiteSpace: 'nowrap' }}>
                      {t('profile.email.sent')}
                    </span>
                  ) : (
                    <button
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: 'var(--primary-color)',
                        cursor: resendingVerification ? 'not-allowed' : 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        textDecoration: 'underline',
                      }}
                    >
                      {resendingVerification ? t('profile.email.sendingVerification') : t('profile.email.resendVerification')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {pendingEmailChange && (
            <div
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: 'var(--success-bg)',
                color: 'var(--success-text)',
                borderRadius: 'var(--radius)',
                marginTop: '1rem',
                fontSize: '0.875rem',
              }}
            >
              {t('profile.email.changeSuccess', { email: pendingEmailChange })}
            </div>
          )}

          {!showEmailChange ? (
            <button
              onClick={() => setShowEmailChange(true)}
              style={{
                ...(isMobile ? buttonStyles.secondarySmall : buttonStyles.secondary),
                marginTop: '1rem',
              }}
            >
              {t('profile.email.changeEmail')}
            </button>
          ) : (
            <form onSubmit={handleEmailChange} style={{ ...formStyle, marginTop: '1rem' }}>
              <div style={formGroupStyle}>
                <label htmlFor="newEmail" className="form-label">
                  {t('profile.email.newEmailAddress')} <span style={{ color: 'var(--error-color)' }}>*</span>
                </label>
                <input
                  id="newEmail"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  style={inputStyle}
                  required
                  disabled={isChangingEmail}
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="confirmNewEmail" className="form-label">
                  {t('profile.email.confirmNewEmail')} <span style={{ color: 'var(--error-color)' }}>*</span>
                </label>
                <input
                  id="confirmNewEmail"
                  type="email"
                  value={confirmNewEmail}
                  onChange={(e) => setConfirmNewEmail(e.target.value)}
                  style={inputStyle}
                  required
                  disabled={isChangingEmail}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="submit"
                  style={{
                    ...(isMobile ? buttonStyles.primarySmall : buttonStyles.primary),
                    fontWeight: '600',
                  }}
                  disabled={isChangingEmail}
                >
                  {isChangingEmail ? t('profile.email.sending') : t('profile.email.sendVerification')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailChange(false);
                    setNewEmail('');
                    setConfirmNewEmail('');
                  }}
                  style={isMobile ? buttonStyles.secondarySmall : buttonStyles.secondary}
                >
                  {t('profile.email.cancel')}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Password Change Section */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>{t('profile.password.title')}</h2>

        <form onSubmit={handlePasswordChange} style={formStyle}>
          {/* Current Password */}
          <div style={formGroupStyle}>
            <label htmlFor="currentPassword" className="form-label">
              {t('profile.password.currentPassword')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={inputStyle}
              required
              disabled={isChangingPassword}
              autoComplete="current-password"
            />
          </div>

          {/* New Password */}
          <div style={formGroupStyle}>
            <label htmlFor="newPassword" className="form-label">
              {t('profile.password.newPassword')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={inputStyle}
              required
              disabled={isChangingPassword}
              autoComplete="new-password"
            />
            <p style={infoTextStyle}>{t('profile.password.minChars')}</p>
          </div>

          {/* Confirm Password */}
          <div style={formGroupStyle}>
            <label htmlFor="confirmPassword" className="form-label">
              {t('profile.password.confirmNewPassword')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
              required
              disabled={isChangingPassword}
              autoComplete="new-password"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            style={{
              ...(isMobile ? buttonStyles.primarySmall : buttonStyles.primary),
              fontWeight: '600',
              alignSelf: 'flex-start',
            }}
            disabled={isChangingPassword}
            onMouseEnter={(e) => {
              if (!isChangingPassword) {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            {isChangingPassword ? t('profile.password.changing') : t('profile.password.changeButton')}
          </button>

          <p style={infoTextStyle}>
            {t('profile.password.logoutWarning')}
          </p>
        </form>
      </div>

      {/* Security Section */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>{t('profile.security.title')}</h2>
        <p style={{ ...infoTextStyle, marginTop: 0, marginBottom: '1rem' }}>
          {t('profile.security.description')}
        </p>
        <Link
          to="/sessions"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--primary-color)',
            textDecoration: 'none',
            fontSize: isMobile ? '0.9375rem' : '1rem',
            fontWeight: '500',
          }}
        >
          {t('profile.security.viewSessions')}
        </Link>
      </div>

      {/* Preferences Section */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>{t('profile.preferences.title')}</h2>
        <div style={formGroupStyle}>
          <label className="form-label">{t('profile.preferences.appearance')}</label>
          <p style={{ ...infoTextStyle, marginTop: 0, marginBottom: '0.75rem' }}>
            {t('profile.preferences.appearanceDescription')}
          </p>
          <ThemeToggle />
        </div>
      </div>

    </div>
  );
};
